import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { SectionMarker } from './SectionMarker';
interface ObjectionItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  delay: number;
}
function ObjectionItem({
  question,
  answer,
  isOpen,
  onToggle,
  delay
}: ObjectionItemProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5,
    delay
  }} className="border border-cream-muted/10 rounded-lg overflow-hidden bg-navy-darkest/50" data-id="element-166">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-6 text-left hover:bg-navy-medium/30 transition-colors" aria-expanded={isOpen} data-id="element-167">
        <span className="font-playfair text-lg md:text-xl italic text-cream pr-4" data-id="element-168">
          "{question}"
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-amber flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-169" />
      </button>

      <AnimatePresence data-id="element-170">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-171">
            <div className="px-6 pb-6 text-cream-muted leading-relaxed" data-id="element-172">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function ObjectionsSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const objections = [{
    question: 'Ya probé todo y nada funciona.',
    answer: <>
          <p className="mb-3" data-id="element-173">Es normal que sientas eso.</p>
          <p className="mb-3" data-id="element-174">
            La mayoría de soluciones trabajan sobre el sueño.
          </p>
          <p className="text-cream" data-id="element-175">
            Este método trabaja sobre el sistema nervioso.
          </p>
          <p className="mt-3 text-amber font-medium" data-id="element-176">Ese es el punto ciego.</p>
        </>
  }, {
    question: '¿Y si tomo medicación?',
    answer: <p data-id="element-177">
          Este material no reemplaza tratamiento médico. Puede utilizarse como
          complemento.
        </p>
  }, {
    question: '¿Y si no tengo tiempo?',
    answer: <p data-id="element-178">
          El protocolo completo toma{' '}
          <span className="text-cream" data-id="element-179">menos de 20 minutos al día</span>.
        </p>
  }, {
    question: '¿Y si no funciona para mí?',
    answer: <>
          <p className="mb-3" data-id="element-180">Nada funciona para el 100% de las personas.</p>
          <p data-id="element-181">
            Pero si aplicas el método durante 21 noches completas, estarás
            dándole a tu sistema nervioso el{' '}
            <span className="text-cream" data-id="element-182">
              tiempo mínimo necesario para adaptarse
            </span>
            .
          </p>
        </>
  }];
  return <section className="w-full bg-navy-darkest py-10 md:py-14" data-id="element-183">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-184">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-12 text-center" data-id="element-185">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-186">
            <SectionMarker data-id="element-187" />
            <span className="text-cream-muted text-sm uppercase tracking-wider" data-id="element-188">
              Objeciones
            </span>
          </div>

          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-cream" data-id="element-189">
            Preguntas frecuentes
          </h2>
        </motion.div>

        <div className="space-y-4" data-id="element-190">
          {objections.map((objection, index) => <ObjectionItem key={index} question={objection.question} answer={objection.answer} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} delay={0.1 * index} data-id="element-191" />)}
        </div>
      </div>
    </section>;
}